import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Star, Calendar, CheckCircle, Clock, FileText, 
  Award, Trophy, TrendingUp, Lock, Eye, Send
} from 'lucide-react';
import { getBackendUrl } from '../utils/config';

// Critérios padrão (fallback se API falhar)
const CRITERIOS_DEFAULT = [
  { id: 'assiduidade_pontualidade', nome: 'Assiduidade e pontualidade', descricao: 'Presença regular e cumprimento de horários' },
  { id: 'cumprimento_tarefas', nome: 'Cumprimento de tarefas', descricao: 'Execução das tarefas atribuídas dentro do prazo' },
  { id: 'atendimento_cliente', nome: 'Atendimento ao cliente', descricao: 'Qualidade no serviço prestado aos clientes' },
  { id: 'trabalho_equipa', nome: 'Trabalho em equipa', descricao: 'Colaboração e relacionamento com colegas' },
  { id: 'iniciativa', nome: 'Iniciativa no trabalho', descricao: 'Proatividade e autonomia na resolução de problemas' },
  { id: 'normas_fardamento', nome: 'Cumprimento das normas internas e uso do fardamento', descricao: 'Respeito pelas regras da empresa e apresentação' }
];

const ESCALA = [
  { valor: 5, label: 'Excelente', cor: 'bg-green-500' },
  { valor: 4, label: 'Bom', cor: 'bg-blue-500' },
  { valor: 3, label: 'Satisfatório', cor: 'bg-yellow-500' },
  { valor: 2, label: 'Insuficiente', cor: 'bg-orange-500' },
  { valor: 1, label: 'Muito insuficiente', cor: 'bg-red-500' }
];

const ESTADOS_AVALIACAO = {
  RASCUNHO: { label: 'Pendente', color: 'bg-gray-100 text-gray-800', icon: Clock },
  AUTO_SUBMETIDA: { label: 'Autoavaliação Submetida', color: 'bg-blue-100 text-blue-800', icon: Send },
  VALIDADA: { label: 'Validada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  BLOQUEADA: { label: 'Finalizada', color: 'bg-purple-100 text-purple-800', icon: Lock }
};

export default function MinhaAvaliacaoDesempenho() {
  const [activeTab, setActiveTab] = useState('atual');
  const [periodoAtivo, setPeriodoAtivo] = useState(null);
  const [avaliacaoAtual, setAvaliacaoAtual] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [resultadosAnuais, setResultadosAnuais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoNotas, setAutoNotas] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [avaliacaoDetalhes, setAvaliacaoDetalhes] = useState(null);
  const [criterios, setCriterios] = useState(CRITERIOS_DEFAULT);

  const token = localStorage.getItem('token');
  const backendUrl = getBackendUrl();

  const fetchDados = useCallback(async () => {
    setLoading(true);
    try {
      // Buscar critérios configurados
      const resCriterios = await fetch(`${backendUrl}/api/avaliacoes-desempenho/criterios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resCriterios.ok) {
        const criteriosData = await resCriterios.json();
        setCriterios(criteriosData.length > 0 ? criteriosData : CRITERIOS_DEFAULT);
      }

      // Buscar período ativo
      const resPeriodo = await fetch(`${backendUrl}/api/avaliacoes-desempenho/periodos/aberto`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resPeriodo.ok) {
        const periodo = await resPeriodo.json();
        setPeriodoAtivo(periodo);
      }

      // Buscar minhas avaliações
      const resAvaliacoes = await fetch(`${backendUrl}/api/avaliacoes-desempenho/semestrais/minhas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resAvaliacoes.ok) {
        const avaliacoes = await resAvaliacoes.json();
        setHistorico(avaliacoes);
        // Encontrar avaliação do período atual
        if (periodoAtivo) {
          const atual = avaliacoes.find(a => a.periodo_id === periodoAtivo?.id);
          if (atual) {
            setAvaliacaoAtual(atual);
            // Preencher notas se já existirem
            if (atual.auto_assiduidade_pontualidade) {
              setAutoNotas({
                auto_assiduidade_pontualidade: atual.auto_assiduidade_pontualidade,
                auto_cumprimento_tarefas: atual.auto_cumprimento_tarefas,
                auto_atendimento_cliente: atual.auto_atendimento_cliente,
                auto_trabalho_equipa: atual.auto_trabalho_equipa,
                auto_iniciativa: atual.auto_iniciativa,
                auto_normas_fardamento: atual.auto_normas_fardamento
              });
            }
          }
        }
      }

      // Buscar resultados anuais
      const resAnuais = await fetch(`${backendUrl}/api/avaliacoes-desempenho/resultados-anuais/meus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resAnuais.ok) {
        setResultadosAnuais(await resAnuais.json());
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
    setLoading(false);
  }, [backendUrl, token, periodoAtivo?.id]);

  useEffect(() => {
    fetchDados();
  }, []);

  // Re-fetch quando período mudar
  useEffect(() => {
    if (periodoAtivo && historico.length > 0) {
      const atual = historico.find(a => a.periodo_id === periodoAtivo.id);
      if (atual) {
        setAvaliacaoAtual(atual);
        if (atual.auto_assiduidade_pontualidade) {
          setAutoNotas({
            auto_assiduidade_pontualidade: atual.auto_assiduidade_pontualidade,
            auto_cumprimento_tarefas: atual.auto_cumprimento_tarefas,
            auto_atendimento_cliente: atual.auto_atendimento_cliente,
            auto_trabalho_equipa: atual.auto_trabalho_equipa,
            auto_iniciativa: atual.auto_iniciativa,
            auto_normas_fardamento: atual.auto_normas_fardamento
          });
        }
      }
    }
  }, [periodoAtivo, historico]);

  const submeterAutoavaliacao = async () => {
    if (!avaliacaoAtual) return;

    // Validar que todas as notas de PONTUAÇÃO estão preenchidas (ignorar tipo texto)
    const criteriosPontuacao = criterios.filter(c => c.tipo !== 'texto');
    const notasPreenchidas = criteriosPontuacao.every(c => {
      const nota = autoNotas[`auto_${c.id}`];
      return nota >= 1 && nota <= 5;
    });
    
    if (!notasPreenchidas) {
      alert('Por favor, preencha todos os critérios de pontuação (1-5) antes de submeter.');
      return;
    }

    if (!window.confirm('Tem a certeza que deseja submeter a sua autoavaliação? Esta ação não pode ser revertida.')) {
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${backendUrl}/api/avaliacoes-desempenho/semestrais/${avaliacaoAtual.id}/autoavaliacao`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(autoNotas)
      });
      if (res.ok) {
        alert('✅ Autoavaliação submetida com sucesso!\n\nA sua avaliação foi enviada para análise.');
        fetchDados();
      } else {
        const error = await res.json();
        alert('❌ ' + (error.detail || 'Erro ao submeter autoavaliação'));
      }
    } catch (error) {
      console.error('Erro ao submeter:', error);
      alert('❌ Erro ao submeter autoavaliação. Tente novamente.');
    }
    setSubmitting(false);
  };

  const tomarConhecimento = async (avaliacaoId) => {
    try {
      const res = await fetch(`${backendUrl}/api/avaliacoes-desempenho/semestrais/${avaliacaoId}/conhecimento`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchDados();
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'atual' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('atual')}
        >
          <Star className="h-4 w-4 mr-1" /> Avaliação Atual
        </Button>
        <Button
          variant={activeTab === 'historico' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('historico')}
        >
          <Calendar className="h-4 w-4 mr-1" /> Histórico
        </Button>
        <Button
          variant={activeTab === 'anual' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('anual')}
        >
          <Award className="h-4 w-4 mr-1" /> Resultados Anuais
        </Button>
        <Button
          variant={activeTab === 'regras' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('regras')}
        >
          <FileText className="h-4 w-4 mr-1" /> Como Funciona
        </Button>
      </div>

      {/* Tab: Avaliação Atual */}
      {activeTab === 'atual' && (
        <div className="space-y-4">
          {!periodoAtivo ? (
            <Card className="bg-gray-50">
              <CardContent className="py-8 text-center">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Não existe período de avaliação aberto de momento.</p>
                <p className="text-sm text-gray-500 mt-2">Será notificado quando um novo período for aberto.</p>
              </CardContent>
            </Card>
          ) : !avaliacaoAtual ? (
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="py-8 text-center">
                <p className="text-yellow-800">A sua avaliação para este período ainda não foi criada.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Info do Período */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">{avaliacaoAtual.periodo_ano} - Semestre {avaliacaoAtual.periodo_semestre}</span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${ESTADOS_AVALIACAO[avaliacaoAtual.estado]?.color}`}>
                      {React.createElement(ESTADOS_AVALIACAO[avaliacaoAtual.estado]?.icon || Clock, { className: 'h-4 w-4' })}
                      {ESTADOS_AVALIACAO[avaliacaoAtual.estado]?.label}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Formulário de Autoavaliação */}
              {avaliacaoAtual.estado === 'RASCUNHO' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="h-5 w-5 text-yellow-500" />
                      A Minha Autoavaliação
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Escala de referência */}
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                      {ESCALA.map(e => (
                        <span key={e.valor} className="flex items-center gap-1 text-sm">
                          <span className={`w-3 h-3 rounded-full ${e.cor}`}></span>
                          {e.valor} - {e.label}
                        </span>
                      ))}
                    </div>

                    {/* Critérios */}
                    <div className="space-y-4">
                      {criterios.map(criterio => (
                        <div key={criterio.id} className="p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">
                                {criterio.nome}
                                {criterio.tipo === 'texto' && <span className="ml-2 text-xs text-blue-500">(campo aberto)</span>}
                              </p>
                              <p className="text-sm text-gray-500">{criterio.descricao}</p>
                            </div>
                          </div>
                          
                          {/* Campo de Pontuação (1-5) */}
                          {criterio.tipo !== 'texto' && (
                            <div className="flex gap-2 mt-3">
                              {ESCALA.map(e => (
                                <button
                                  key={e.valor}
                                  onClick={() => setAutoNotas(prev => ({...prev, [`auto_${criterio.id}`]: e.valor}))}
                                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                                    autoNotas[`auto_${criterio.id}`] === e.valor
                                      ? `${e.cor} text-white shadow-md scale-105`
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  {e.valor}
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Campo de Texto Aberto */}
                          {criterio.tipo === 'texto' && (
                            <textarea
                              value={autoNotas[`auto_${criterio.id}`] || ''}
                              onChange={e => setAutoNotas(prev => ({...prev, [`auto_${criterio.id}`]: e.target.value}))}
                              placeholder="Escreva a sua resposta..."
                              className="w-full mt-3 p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              rows={3}
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Botão Submeter */}
                    <div className="flex justify-end pt-4 border-t">
                      <Button 
                        onClick={submeterAutoavaliacao}
                        disabled={submitting}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {submitting ? (
                          <>A submeter...</>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Submeter Autoavaliação
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Resultado da Avaliação (após validação) */}
              {['AUTO_SUBMETIDA', 'VALIDADA', 'BLOQUEADA'].includes(avaliacaoAtual.estado) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      Resultado da Avaliação
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Autoavaliação submetida */}
                    <div>
                      <h4 className="font-semibold mb-3">A Minha Autoavaliação</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {criterios.filter(c => c.tipo !== 'texto').map(criterio => (
                          <div key={criterio.id} className="p-2 bg-blue-50 rounded">
                            <p className="text-xs text-gray-600">{criterio.nome}</p>
                            <p className="font-bold text-blue-700">
                              {avaliacaoAtual[`auto_${criterio.id}`] || '-'} {avaliacaoAtual[`auto_${criterio.id}`] ? `- ${ESCALA.find(e => e.valor === avaliacaoAtual[`auto_${criterio.id}`])?.label || ''}` : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                      {/* Campos de Texto */}
                      {criterios.filter(c => c.tipo === 'texto').map(criterio => (
                        <div key={criterio.id} className="mt-3 p-3 bg-blue-50 rounded">
                          <p className="text-xs text-gray-600 font-medium">{criterio.nome}</p>
                          <p className="text-sm text-blue-800 mt-1">
                            {avaliacaoAtual[`auto_${criterio.id}`] || <span className="text-gray-400 italic">Não preenchido</span>}
                          </p>
                        </div>
                      ))}
                      {avaliacaoAtual.auto_media && (
                        <p className="mt-2 text-right font-medium">
                          Média: <span className="text-blue-600">{avaliacaoAtual.auto_media.toFixed(2)}</span>
                        </p>
                      )}
                    </div>

                    {/* Heteroavaliação (se validada) */}
                    {avaliacaoAtual.hetero_media && (
                      <div>
                        <h4 className="font-semibold mb-3">Avaliação da Gestão</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {criterios.filter(c => c.tipo !== 'texto').map(criterio => (
                            <div key={criterio.id} className="p-2 bg-green-50 rounded">
                              <p className="text-xs text-gray-600">{criterio.nome}</p>
                              <p className="font-bold text-green-700">
                                {avaliacaoAtual[`hetero_${criterio.id}`] || '-'} {avaliacaoAtual[`hetero_${criterio.id}`] ? `- ${ESCALA.find(e => e.valor === avaliacaoAtual[`hetero_${criterio.id}`])?.label || ''}` : ''}
                              </p>
                            </div>
                          ))}
                        </div>
                        {/* Campos de Texto - Hetero */}
                        {criterios.filter(c => c.tipo === 'texto').map(criterio => (
                          <div key={criterio.id} className="mt-3 p-3 bg-green-50 rounded">
                            <p className="text-xs text-gray-600 font-medium">{criterio.nome}</p>
                            <p className="text-sm text-green-800 mt-1">
                              {avaliacaoAtual[`hetero_${criterio.id}`] || <span className="text-gray-400 italic">Não preenchido</span>}
                            </p>
                          </div>
                        ))}
                        <p className="mt-2 text-right font-medium">
                          Média: <span className="text-green-600">{avaliacaoAtual.hetero_media.toFixed(2)}</span>
                        </p>
                        {avaliacaoAtual.hetero_fundamentacao && (
                          <div className="mt-3 p-3 bg-gray-50 rounded">
                            <p className="text-sm font-medium text-gray-700">Fundamentação:</p>
                            <p className="text-sm text-gray-600">{avaliacaoAtual.hetero_fundamentacao}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Resumo Final */}
                    {avaliacaoAtual.nota_final_semestral && (
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                        <h4 className="font-semibold mb-3">Resumo Final</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Nota Final</p>
                            <p className="text-2xl font-bold text-blue-600">{avaliacaoAtual.nota_final_semestral.toFixed(2)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Pontuação Base</p>
                            <p className="text-xl font-bold">{avaliacaoAtual.pontuacao_base}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Bónus Ranking</p>
                            <p className="text-xl font-bold text-yellow-600">+{avaliacaoAtual.bonus_ranking}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Pontuação Final</p>
                            <p className="text-2xl font-bold text-green-600">{avaliacaoAtual.pontuacao_semestral_final}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Botão Tomar Conhecimento */}
                    {avaliacaoAtual.estado === 'VALIDADA' && !avaliacaoAtual.tomou_conhecimento && (
                      <div className="flex justify-end pt-4 border-t">
                        <Button onClick={() => tomarConhecimento(avaliacaoAtual.id)}>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Tomei Conhecimento
                        </Button>
                      </div>
                    )}

                    {avaliacaoAtual.tomou_conhecimento && (
                      <p className="text-sm text-green-600 text-right">
                        ✓ Tomou conhecimento em {new Date(avaliacaoAtual.tomou_conhecimento_em).toLocaleDateString('pt-PT')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Estado: A aguardar avaliação da gestão */}
              {avaliacaoAtual.estado === 'AUTO_SUBMETIDA' && !avaliacaoAtual.hetero_media && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="py-6 text-center">
                    <Clock className="h-10 w-10 text-blue-600 mx-auto mb-3" />
                    <p className="text-blue-800 font-medium">A sua autoavaliação foi submetida com sucesso!</p>
                    <p className="text-sm text-blue-600 mt-2">A aguardar avaliação da gestão...</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Tab: Histórico */}
      {activeTab === 'historico' && (
        <div className="space-y-3">
          {historico.length === 0 ? (
            <Card className="bg-gray-50">
              <CardContent className="py-8 text-center">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Ainda não tem avaliações no histórico.</p>
              </CardContent>
            </Card>
          ) : (
            historico.map(aval => (
              <Card key={aval.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold">{aval.periodo_ano} - Semestre {aval.periodo_semestre}</p>
                      <div className="flex items-center gap-3 mt-1 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${ESTADOS_AVALIACAO[aval.estado]?.color}`}>
                          {ESTADOS_AVALIACAO[aval.estado]?.label}
                        </span>
                        {aval.nota_final_semestral && (
                          <span className="text-blue-600">Nota: {aval.nota_final_semestral.toFixed(2)}</span>
                        )}
                        {aval.pontuacao_semestral_final && (
                          <span className="text-green-600">Pontos: {aval.pontuacao_semestral_final}</span>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setAvaliacaoDetalhes(aval)}>
                      <Eye className="h-4 w-4 mr-1" /> Ver Detalhes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Tab: Resultados Anuais */}
      {activeTab === 'anual' && (
        <div className="space-y-3">
          {resultadosAnuais.length === 0 ? (
            <Card className="bg-gray-50">
              <CardContent className="py-8 text-center">
                <Award className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Ainda não tem resultados anuais.</p>
              </CardContent>
            </Card>
          ) : (
            resultadosAnuais.map(res => (
              <Card key={res.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-bold text-lg">{res.ano}</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                      res.nivel_atribuido === 'N3' ? 'bg-yellow-100 text-yellow-800' :
                      res.nivel_atribuido === 'N2' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      Nível {res.nivel_atribuido}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Média Anual</p>
                      <p className="font-bold text-blue-600">{res.media_anual?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Pontuação Anual</p>
                      <p className="font-bold">{res.pontuacao_anual}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Prémio Anual</p>
                      <p className="font-bold text-green-600">{res.premio_anual > 0 ? `${res.premio_anual}€` : '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Férias Adicionais</p>
                      <p className="font-bold">{res.ferias_adicionais_internas} dia(s)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Tab: Regras / Como Funciona */}
      {activeTab === 'regras' && (
        <div className="space-y-4">
          {/* Cálculo da Nota */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                Como é Calculada a Nota Final
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                <p className="text-center font-mono text-lg">
                  <strong>Nota Final</strong> = (Autoavaliação × <span className="text-blue-600">40%</span>) + (Avaliação da Gestão × <span className="text-green-600">60%</span>)
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-sm">
                <div className="p-3 bg-green-100 rounded-lg">
                  <p className="font-bold text-green-700">≥ 4.5</p>
                  <p className="text-gray-600">Excelente</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <p className="font-bold text-blue-700">4.0 - 4.4</p>
                  <p className="text-gray-600">Bom</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <p className="font-bold text-yellow-700">3.0 - 3.9</p>
                  <p className="text-gray-600">Satisfatório</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <p className="font-bold text-red-700">&lt; 3.0</p>
                  <p className="text-gray-600">Insuficiente</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Progressão de Nível */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Progressão de Nível
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-bold text-gray-800 mb-2">📊 Condições para Subir de Nível:</h4>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    <li>Média anual ≥ <strong>4.5</strong></li>
                    <li>Zero advertências formais no ano</li>
                  </ul>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-100 rounded-lg border-2 border-gray-300">
                    <h4 className="font-bold text-gray-700 text-center mb-2">Nível N1</h4>
                    <p className="text-sm text-center text-gray-600">Nível inicial</p>
                    <p className="text-center text-gray-500 text-xs mt-2">Sem prémio</p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                    <h4 className="font-bold text-blue-700 text-center mb-2">⬆️ Nível N2</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Após <strong>24 meses</strong> na empresa</li>
                      <li>• Cumprir condições base</li>
                    </ul>
                    <p className="text-center font-bold text-blue-800 mt-2">Prémio: 480€/ano</p>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-400">
                    <h4 className="font-bold text-yellow-700 text-center mb-2">⬆️ Nível N3</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Após <strong>24 meses</strong> em N2</li>
                      <li>• Cumprir condições base</li>
                    </ul>
                    <p className="text-center font-bold text-yellow-800 mt-2">Prémio: 960€/ano</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Férias Adicionais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-green-600" />
                Férias Adicionais Internas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Com base na média anual, pode receber dias de férias adicionais além das férias legais:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg text-center border-2 border-green-300">
                  <p className="text-3xl font-bold text-green-600">+2 dias</p>
                  <p className="text-sm text-gray-600 mt-2">Média anual ≥ 4.5</p>
                  <p className="text-xs text-green-600">(Excelente desempenho)</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center border-2 border-blue-300">
                  <p className="text-3xl font-bold text-blue-600">+1 dia</p>
                  <p className="text-sm text-gray-600 mt-2">Média anual ≥ 4.0</p>
                  <p className="text-xs text-blue-600">(Bom desempenho)</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center border-2 border-gray-300">
                  <p className="text-3xl font-bold text-gray-600">0 dias</p>
                  <p className="text-sm text-gray-600 mt-2">Média anual &lt; 4.0</p>
                  <p className="text-xs text-gray-500">(Sem férias adicionais)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bónus de Ranking */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Bónus de Ranking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  Se ficares em <strong>1º ou 2º lugar</strong> no ranking mensal (com mais de 20 pontos) 
                  pelo menos <strong>3 vezes</strong> no semestre, recebes um bónus de <span className="font-bold text-green-600">+2 pontos</span> na avaliação!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Critérios de Avaliação */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                Os 6 Critérios de Avaliação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-3">
                {criterios.map((criterio, index) => (
                  <div key={criterio.id} className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-800">
                      {index + 1}. {criterio.nome}
                    </p>
                    <p className="text-xs text-gray-500">{criterio.descricao}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Escala:</strong> 5 (Excelente) | 4 (Bom) | 3 (Satisfatório) | 2 (Insuficiente) | 1 (Muito insuficiente)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Detalhes da Avaliação */}
      {avaliacaoDetalhes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="sticky top-0 bg-white z-10 border-b">
              <div className="flex items-center justify-between">
                <CardTitle>
                  Avaliação {avaliacaoDetalhes.periodo_ano} - Semestre {avaliacaoDetalhes.periodo_semestre}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setAvaliacaoDetalhes(null)}>✕</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              {/* Estado */}
              <div className="flex justify-center">
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${ESTADOS_AVALIACAO[avaliacaoDetalhes.estado]?.color}`}>
                  {ESTADOS_AVALIACAO[avaliacaoDetalhes.estado]?.label}
                </span>
              </div>

              {/* Autoavaliação */}
              {avaliacaoDetalhes.auto_media && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    A Minha Autoavaliação (Média: {avaliacaoDetalhes.auto_media.toFixed(2)})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {criterios.filter(c => c.tipo !== 'texto').map(criterio => (
                      <div key={criterio.id} className="p-2 bg-blue-50 rounded">
                        <p className="text-xs text-gray-600">{criterio.nome}</p>
                        <p className="font-bold text-blue-700">
                          {avaliacaoDetalhes[`auto_${criterio.id}`] || '-'} 
                          {avaliacaoDetalhes[`auto_${criterio.id}`] && (
                            <span className="text-xs font-normal text-gray-500 ml-1">
                              ({ESCALA.find(e => e.valor === avaliacaoDetalhes[`auto_${criterio.id}`])?.label})
                            </span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                  {/* Campos de Texto */}
                  {criterios.filter(c => c.tipo === 'texto').map(criterio => (
                    <div key={criterio.id} className="mt-3 p-3 bg-blue-50 rounded">
                      <p className="text-xs text-gray-600 font-medium">{criterio.nome}</p>
                      <p className="text-sm text-blue-800 mt-1">
                        {avaliacaoDetalhes[`auto_${criterio.id}`] || <span className="text-gray-400 italic">Não preenchido</span>}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Heteroavaliação */}
              {avaliacaoDetalhes.hetero_media && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Award className="h-4 w-4 text-green-500" />
                    Avaliação da Gestão (Média: {avaliacaoDetalhes.hetero_media.toFixed(2)})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {criterios.filter(c => c.tipo !== 'texto').map(criterio => (
                      <div key={criterio.id} className="p-2 bg-green-50 rounded">
                        <p className="text-xs text-gray-600">{criterio.nome}</p>
                        <p className="font-bold text-green-700">
                          {avaliacaoDetalhes[`hetero_${criterio.id}`] || '-'}
                          {avaliacaoDetalhes[`hetero_${criterio.id}`] && (
                            <span className="text-xs font-normal text-gray-500 ml-1">
                              ({ESCALA.find(e => e.valor === avaliacaoDetalhes[`hetero_${criterio.id}`])?.label})
                            </span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                  {/* Campos de Texto - Hetero */}
                  {criterios.filter(c => c.tipo === 'texto').map(criterio => (
                    <div key={criterio.id} className="mt-3 p-3 bg-green-50 rounded">
                      <p className="text-xs text-gray-600 font-medium">{criterio.nome}</p>
                      <p className="text-sm text-green-800 mt-1">
                        {avaliacaoDetalhes[`hetero_${criterio.id}`] || <span className="text-gray-400 italic">Não preenchido</span>}
                      </p>
                    </div>
                  ))}
                  {avaliacaoDetalhes.hetero_fundamentacao && (
                    <div className="mt-3 p-3 bg-gray-50 rounded">
                      <p className="text-sm font-medium text-gray-700">Fundamentação:</p>
                      <p className="text-sm text-gray-600">{avaliacaoDetalhes.hetero_fundamentacao}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Resumo Final */}
              {avaliacaoDetalhes.nota_final_semestral && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                  <h4 className="font-semibold mb-3">Resumo Final</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-600">Nota Final</p>
                      <p className="text-2xl font-bold text-blue-600">{avaliacaoDetalhes.nota_final_semestral.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Pontuação Base</p>
                      <p className="text-xl font-bold">{avaliacaoDetalhes.pontuacao_base || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Bónus</p>
                      <p className="text-xl font-bold text-green-600">
                        +{(avaliacaoDetalhes.bonus_ranking || 0) + (avaliacaoDetalhes.bonus_antiguidade || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Pontuação Final</p>
                      <p className="text-2xl font-bold text-purple-600">{avaliacaoDetalhes.pontuacao_semestral_final || 0}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Botão Fechar */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={() => setAvaliacaoDetalhes(null)}>Fechar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
